var $name="video/index",$path="app/modules/video/index.js",$this={$name,$path,};import shell from 'app/modules/shell';

import {
  checkIfYoutubeLink,
  checkIfYouKuLink,
  defaultPlayerVariables,
  getBokeCCSrcLink,
  getCombinedPlayerVariables,
  getYouKuSrcLink,
  initBokeCCPlayer,
  initYouKuPlayer,
  isBokeCCLink,
  isYouKuLink,
} from 'app/modules/video/utils';

import getVideoId from 'app/utilities/getVideoId';

import { countryCodes, css, delays } from 'configs';

import first from 'lodash/first';
import forEach from 'lodash/forEach';
import keys from 'lodash/keys';

import {
  playerTypeDataAttr,
  youtubeMarker,
  youkuMarker,
  embeddedPlayerNodeSelector,
  iframeYouTubeSelector,
  iframeYoukuSelector,
} from './config';

export default shell.registerModule($this, ({ addEventListener, settings }) => {
  const playVideoEvent = new Event('playVideo');

  const YOUTUBE_API_LINK = 'https://www.youtube.com/iframe_api';
  const youTubeItems = [];
  let isVideoLibApiLoaded = false;

  const getYoutubeScript = () => document.querySelector('script[src*="youtube.com/iframe_api"]');

  const iFrameScriptLoader = () => {
    if (!isVideoLibApiLoaded && !getYoutubeScript() && settings.page.countryCode !== countryCodes.china) {
      const tag = document.createElement('script');
      tag.src = YOUTUBE_API_LINK;
      tag.setAttribute('nonce', 'sgo3un1q3uu3qskh');
      const firstScriptTag = first(document.getElementsByTagName('script'));
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
    isVideoLibApiLoaded = true;
  };

  const initPlayer = (embeddedPlayer) => {
    if (embeddedPlayer && window.YT) {
      const player = new window.YT.Player(embeddedPlayer.node, {
        videoId: embeddedPlayer.videoId || '',
        events: embeddedPlayer.events,
        playerVariables: embeddedPlayer.playerVariables || {},
        width: embeddedPlayer.playerVariables.width,
      });

      if (embeddedPlayer.events.onInit) {
        embeddedPlayer.events.onInit(player);
      }
    }
  };

  const onPlayerReady = (event) => {
    event.target.playVideo();
  };

  const isYouTubeLoaded = () => isVideoLibApiLoaded
    || getYoutubeScript()
    || (window.YT && window.YT.loaded);

  function initYouTube({ embeddedPlayerElements, events, playerVariables: vars, playerButtons: buttons }) {
    const eventCallbacks = {
      onInit: [],
      onReady: [],
      onStateChange: [],
      onPlaybackQualityChange: [],
      onError: [],
    };

    const playerButtons = buttons || [];

    const combineEventHandlers = () => {
      const combinedHandlers = {};

      forEach(
        keys(eventCallbacks),
        (key) => {
          if (events && events[key]) {
            eventCallbacks[key].push(events[key]);
          }

          if (eventCallbacks[key].length) {
            combinedHandlers[key] = (event) => {
              forEach(eventCallbacks[key], (callback) => callback(event));
            };
          }
        },
      );

      return combinedHandlers;
    };

    if (embeddedPlayerElements.length) {
      // It is necessary to push every player's config into "youTubeConfigsList" array.
      // It will be processed after YouTube API loading by colling "onYouTubeIframeAPIReady" global method.
      // eslint-disable-next-line consistent-return
      forEach(
        embeddedPlayerElements,
        (embeddedPlayerNode) => {
          const playerVariables = getCombinedPlayerVariables(embeddedPlayerNode, vars);

          if (isYouKuLink(embeddedPlayerNode)) {
            initYouKuPlayer(embeddedPlayerNode);
          }
          if (isBokeCCLink(embeddedPlayerNode)) {
            initBokeCCPlayer(embeddedPlayerNode);
          }

          // Everything about Player Variables you can find here:
          // https://developers.google.com/youtube/player_parameters?hl=ru
          const playerConfig = {
            node: embeddedPlayerNode,
            videoId: getVideoId(embeddedPlayerNode),
            events: combineEventHandlers(),
            playerVariables,
          };

          forEach(playerButtons, (button) => addEventListener(button, 'click', iFrameScriptLoader));

          if (events?.onReady) {
            isYouTubeLoaded()
              ? initPlayer(playerConfig)
              : youTubeItems.push(playerConfig);
          }

          eventCallbacks.onReady.push(onPlayerReady);
        },
      );
    }
  }

  function setQueryParams(urlString) {
    urlString = urlString.replace('autoStart=false', 'autoStart=true');

    const url = new URL(urlString);

    forEach(
      keys(defaultPlayerVariables),
      (key) => {
        url.searchParams.set(key, defaultPlayerVariables[key]);
      },
    );

    return url.href;
  }

  function getIframeMarkup(isYouTube) {
    return `<iframe
      class="${isYouTube ? 'sdl-ratio-target video-youtube-iframe' : 'youku-iframe video-youku-iframe'}"
      ${isYouTube ? 'src=""' : 'title="youku-iframe"'}
      allow="autoplay"
      allowfullscreen="true"
    ></iframe>`;
  }

  function bindOpenModalVideo(source, isYouTube) {
    return async (e) => {
      e.preventDefault();

      const modal = (await import('app/partials/modal')).default;
      const modalElement = modal.showModal({
        isVideo: true,
        appendHTML: getIframeMarkup(isYouTube),
        labelCloseButton: e.target.dataset.videoCloseLabel,
        labelExpandButton: e.target.dataset.videoExpandLabel,
        target: e.target,
      });

      const fullSource = setQueryParams(source);
      const iframe = modalElement.querySelector(`${isYouTube ? iframeYouTubeSelector : iframeYoukuSelector}`);

      iframe.dataset.videolink = source;

      if (fullSource !== iframe.getAttribute('src')) {
        iframe.setAttribute('src', fullSource);
        iframe.dataset[playerTypeDataAttr] = isYouTube ? youtubeMarker : youkuMarker;
      } else if (isYouTube) {
        iframe.setAttribute('src', fullSource);

        modalElement.dispatchEvent(playVideoEvent);
      }
    };
  }

  const handleOpenModalButtons = (element, videoButtonSelector = `.${css.classNames.videoPlayButton}`) => {
    const allVideoButtons = element.querySelectorAll(videoButtonSelector);
    if (!allVideoButtons.length) return;

    forEach(
      allVideoButtons,
      (button) => {
        let videoUrl = button?.dataset.href || button?.dataset.videolink || '';
        if (!videoUrl.length) return;
        const isYouTube = checkIfYoutubeLink(videoUrl);
        if (!isYouTube && checkIfYouKuLink(videoUrl)) {
          videoUrl = getYouKuSrcLink(videoUrl);
        } else if (!isYouTube) {
          videoUrl = getBokeCCSrcLink(videoUrl);
        }

        const eventHandler = bindOpenModalVideo(videoUrl, isYouTube);
        addEventListener(button, 'click', (event) => {
          const intervalId = setInterval(() => {
            if ((!isYouTube && isVideoLibApiLoaded) || (isYouTube && window.YT.loaded)) {
              eventHandler(event);
              clearInterval(intervalId);
            }
          }, delays.ms500);
        });
      },
    );

    const youtubeVideoPlayerPlaceholder = document.body.querySelector(embeddedPlayerNodeSelector);

    // It's necessary to config video src link to show subtitles if it's expected
    // eslint-disable-next-line no-unused-vars
    const ytPlayerVariables = getCombinedPlayerVariables(youtubeVideoPlayerPlaceholder);

    initYouTube({
      embeddedPlayerElements: allVideoButtons,
      playerButtons: allVideoButtons || [],
    });
  };

  // This is pre-requested method name which called by YouTube API "on ready".
  window.onYouTubeIframeAPIReady = () => forEach(youTubeItems, initPlayer);

  return { handleOpenModalButtons };
});
